<template>
    <app-dialog
        v-if="tranche"
        v-model="isActive"
        :persistent="tranche.$loading"
        size="large"
        :title="$t(`enums.tranche_action.${actionId}`)"
        :overline="$t('tranche')"
    >
        <template #default>
            <transition appear name="fade-transition" mode="out-in">
                <template v-if="TrancheActionEnum.DELETE_TRANCHE === actionId">
                    <v-card-text class="px-6 pt-1">
                        <app-alert alert-type="error" dense :title="$t('danger_this_action_cannot_be_undone')">
                            <template #text>
                                <p class="mt-1 mb-2 body-2 error--text text--darken-2">
                                    Deleting this holding will cascade through the system and remove all holdings,
                                    transactions, media files and activity entries linked to this tranche. Executing
                                    this incorrectly cannot be undone and will required you to re-book ALL the
                                    transactions required to rebuild this tranche.
                                </p>

                                <p class="mb-0 body-2 error--text text--darken-2">
                                    This does NOT remove the Fund Account for the tranche.
                                </p>

                                <p class="mb-0 body-2 error--text text--darken-2">
                                    Check you have the right details below before proceeding.
                                </p>
                            </template>
                        </app-alert>

                        <h3 class="font-weight-normal mt-6">{{ $t('tranche') }} {{ $t('information') }}</h3>
                        <app-table-simple table-layout="auto" column-width-auto outlined>
                            <template #body>
                                <tr>
                                    <td>{{ $t('id') }}</td>
                                    <td>{{ tranche.id }}</td>
                                </tr>
                                <tr v-if="tranche.fund">
                                    <td>{{ $t('fund') }}</td>
                                    <td>{{ tranche.fund.name }}</td>
                                </tr>
                                <tr>
                                    <td>{{ $t('verified') }}</td>
                                    <td><display-boolean :value="tranche.reconciled" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('discretionary') }}</td>
                                    <td><display-boolean :value="tranche.discretionary" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('multi_investment') }}</td>
                                    <td><display-boolean :value="tranche.multi_investment" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('cash_balance') }}</td>
                                    <td><display-money :value="tranche.cash_balance" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('total_investment') }}</td>
                                    <td><display-money :value="tranche.total_investment" /></td>
                                </tr>
                                <tr>
                                    <td>{{ $t('invested_on') }}</td>
                                    <td><display-date :value="tranche.invested_on" /></td>
                                </tr>
                                <tr v-if="tranche.investor">
                                    <td>{{ $t('investor') }}</td>
                                    <td>{{ tranche.investor.name }}</td>
                                </tr>
                                <tr v-if="tranche.adviser">
                                    <td>{{ $t('adviser') }}</td>
                                    <td>{{ tranche.adviser.name }}</td>
                                </tr>
                            </template>
                        </app-table-simple>
                    </v-card-text>
                </template>

                <template v-else>
                    <v-card-text class="px-6 pt-1">
                        <p>Action {{ actionId }} is still in development, please come back later.</p>
                    </v-card-text>
                </template>
            </transition>
        </template>
        <template #actions>
            <v-spacer />

            <app-button
                secondary
                :text="$t('cancel')"
                class="mr-2"
                :disabled="tranche.$loading"
                @click="isActive = false"
            />

            <app-button
                v-if="TrancheActionEnum.DELETE_TRANCHE === actionId"
                :text="$t('delete')"
                :icon="getIcon('error')"
                :loading="tranche.$loading"
                class="ml-2"
                color="error"
                @click="deleteTranche"
            />

            <app-button
                v-else
                :text="$t('save')"
                :icon="getIcon('save', 'fas')"
                :loading="tranche.$loading"
                class="ml-2"
                @click="saveTranche"
            />
        </template>
    </app-dialog>
</template>

<script>
import { Tranche } from '@/models';
import TrancheActionEnum from '@/enums/tranche/action';
import snack from '@/lib/helpers/snack';
import validate from '@/mixins/validate';
import dialog from '@/lib/helpers/dialog';

export default {
    name: 'TrancheActionModal',
    components: {
        AppDialog: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppDialog'
            ),
        AppTableSimple: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/table/AppTableSimple'
            )
    },
    mixins: [validate],
    props: {
        value: {
            type: Boolean,
            required: true
        },
        actionId: {
            type: String,
            required: true
        },
        trancheId: {
            type: String,
            required: true
        }
    },
    data: () => ({
        tranche: new Tranche(),
        TrancheActionEnum
    }),
    computed: {
        isActive: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        }
    },
    watch: {
        trancheId: {
            immediate: true,
            handler(id) {
                this.setTranche(id);
            }
        }
    },
    methods: {
        async setTranche(id) {
            const tranche = await Tranche.$get(id);
            this.$emit('input:tranche', new Tranche(tranche.$toPayload()));

            this.tranche = tranche;
        },
        async deleteTranche() {
            this.tranche.$loading = true;

            const removeItem = async () => {
                try {
                    await this.tranche.$remove();
                    this.$emit('delete:tranche', this.tranche);
                    this.isActive = false;
                    snack({
                        timeout: 20000,
                        type: 'success',
                        text: `${this.$t('tranche')} ${this.$t('deleted')}`
                    });
                } catch (error) {
                    this.handleError(error, removeItem);
                }
                this.tranche.$loading = false;
            };

            dialog({
                continueButtonColor: 'error',
                continueButtonIcon: 'error',
                continueButtonText: this.$t('delete'),
                cancelButtonText: this.$t('cancel'),
                title: this.$t('are_you_sure'),
                text:
                    this.$t('proceeding_will_permanently_delete') +
                    ' ' +
                    this.$t('tranche').toLocaleLowerCase() +
                    ' ' +
                    this.tranche.id +
                    '.',
                onContinueFn: removeItem,
                onCancelFn: () => {
                    this.tranche.$loading = false;
                }
            });
        }
    }
};
</script>
